import {
	CustomersIcon,
	DashboardIcon,
	ListIcon,
	LogsIcon,
	ShieldIcon,
	UserManageSolidIcon,
	WebhooksIcon,
	WrenchScrewdriverSolidIcon,
} from "@moznsystems/mozn-ui";
import { FC, FunctionComponent, SVGProps } from "react";
import { CustomFieldsIcon } from "@/components/icons/custom-fields";
import { DeviceIcon } from "@/components/icons/device";
import { KeyIcon } from "@/components/icons/key";
import { useAuthStore } from "@/store/authStore";
import { FliptFlagType, RoleValues } from "@/types";
import {
	ACCOUNT_SETTINGS,
	API_KEYS,
	CUSTOM_FIELDS,
	CUSTOMERS,
	DASHBOARD,
	DEVICES,
	EVENTS,
	FLIPT_FLAGS,
	LISTS,
	LOGS,
	ROLES,
	RULES,
	TEAMS_MEMBERS,
	WEBHOOKS,
} from "./constants";
import {
	ACCOUNT_SETTINGS_ROUTE,
	API_KEYS_ROUTE,
	CUSTOM_FIELDS_ROUTE,
	CUSTOMERS_ROUTE,
	DASHBOARD_ROUTE,
	DEVICES_ROUTE,
	EVENTS_ROUTE,
	LISTS_ROUTE,
	LOGS_ROUTE,
	RULES_ROUTE,
	TEAM_MANAGEMENT_ROUTE,
	WEBHOOKS_ROUTE,
} from "./routes";

export const GROUPS = {
	MAIN: "Main",
	CONFIGURATION: "Configuration",
	ORGANIZATION: "Organization",
	REPORTS: "Reports",
	DEVELOPER: "Developer",
};

export type NavigationType = {
	group: string;
	label: string;
	flag?: keyof typeof FLIPT_FLAGS;
	url: string;
	routes?: string[];
	role?: RoleValues;
	icon:
		| FunctionComponent<
				SVGProps<SVGSVGElement> & {
					version?: "1" | "2";
				}
		  >
		| FC<SVGProps<SVGSVGElement>>;
	subItems: SubNavigationType[];
};

type SubNavigationType = {
	label: string;
	url: string;
	role?: keyof typeof ROLES;
	flag?: keyof typeof FLIPT_FLAGS;
	routes: string[];
};

const navigation: NavigationType[] = [
	{
		group: GROUPS.MAIN,
		label: DASHBOARD,
		url: DASHBOARD_ROUTE,
		icon: DashboardIcon,
		subItems: [],
	},
	{
		group: GROUPS.MAIN,
		label: EVENTS,
		url: EVENTS_ROUTE,
		icon: ShieldIcon,
		subItems: [],
	},
	{
		group: GROUPS.MAIN,
		label: CUSTOMERS,
		url: CUSTOMERS_ROUTE,
		icon: CustomersIcon,
		subItems: [],
	},
	{
		group: GROUPS.MAIN,
		label: DEVICES,
		url: DEVICES_ROUTE,
		icon: DeviceIcon,
		subItems: [],
	},
	{
		group: GROUPS.CONFIGURATION,
		label: RULES,
		url: RULES_ROUTE,
		role: ROLES.ADMIN,
		icon: WrenchScrewdriverSolidIcon,
		subItems: [],
	},
	{
		group: GROUPS.CONFIGURATION,
		label: CUSTOM_FIELDS,
		url: CUSTOM_FIELDS_ROUTE,
		role: ROLES.ADMIN,
		icon: CustomFieldsIcon,
		subItems: [],
	},
	{
		group: GROUPS.CONFIGURATION,
		label: LISTS,
		url: LISTS_ROUTE,
		icon: ListIcon,
		subItems: [],
	},
	{
		group: GROUPS.ORGANIZATION,
		label: ACCOUNT_SETTINGS,
		url: ACCOUNT_SETTINGS_ROUTE,
		icon: UserManageSolidIcon,
		subItems: [],
	},
	{
		group: GROUPS.ORGANIZATION,
		label: TEAMS_MEMBERS,
		url: TEAM_MANAGEMENT_ROUTE,
		role: ROLES.ADMIN,
		icon: CustomersIcon,
		subItems: [],
	},
	{
		group: GROUPS.REPORTS,
		label: LOGS,
		url: LOGS_ROUTE,
		role: ROLES.ADMIN,
		icon: LogsIcon,
		subItems: [],
	},
	{
		group: GROUPS.DEVELOPER,
		label: API_KEYS,
		url: API_KEYS_ROUTE,
		role: ROLES.ADMIN,
		icon: KeyIcon,
		subItems: [],
	},
	{
		group: GROUPS.DEVELOPER,
		label: WEBHOOKS,
		flag: "webhooks-integration",
		url: WEBHOOKS_ROUTE,
		role: ROLES.ADMIN,
		icon: WebhooksIcon,
		subItems: [],
	},
];

const canBeIncludedInNavList = (
	role: string,
	navItem: NavigationType | SubNavigationType,
	flags?: FliptFlagType,
) => {
	if (navItem.flag && !flags?.[navItem.flag]) {
		return false;
	}

	if (navItem.role && role !== navItem.role) {
		return false;
	}

	return true;
};

const DEVICE_ONLY_URLS = [
	ACCOUNT_SETTINGS_ROUTE,
	TEAM_MANAGEMENT_ROUTE,
	LOGS_ROUTE,
	API_KEYS_ROUTE,
];

const isAllowedInDeviceFingerprintOnly = (
	url: string,
	deviceFingerprintOnly: boolean,
) => !deviceFingerprintOnly || DEVICE_ONLY_URLS.includes(url);

export const getNavigationItems = () => {
	const { user } = useAuthStore();

	const deviceFingerprintOnly =
		Array.isArray(user?.subscriptions) &&
		user!.subscriptions.length === 1 &&
		(user!.subscriptions as unknown as string[])[0] === "device-fingerprint";

	return navigation
		.filter(
			(navItem) =>
				canBeIncludedInNavList(user?.role, navItem, user?.flags) &&
				isAllowedInDeviceFingerprintOnly(navItem.url, deviceFingerprintOnly),
		)
		.map((navItem) => {
			if (navItem.subItems) {
				const filteredSubItems = navItem.subItems.filter(
					(subItem) =>
						canBeIncludedInNavList(user?.role, subItem, user?.flags) &&
						isAllowedInDeviceFingerprintOnly(
							subItem.url,
							deviceFingerprintOnly,
						),
				);
				navItem.subItems = filteredSubItems;
			}
			return navItem;
		});
};
